import axios from 'axios';

// Dynamic API URL - runtime config with dev VM fallback
// On dev VMs, always use HTTP:5213 regardless of frontend protocol
const API_BASE_URL = window.__config__?.API_BASE_URL
  || (window.location.hostname === 'localhost'
      ? 'http://localhost:5213'
      : `http://${window.location.hostname}:5213`);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Inject Bearer token for Entra auth mode
api.interceptors.request.use(async (config) => {
  const authMode = window.__config__?.AUTH_MODE || 'impersonation';
  if (authMode === 'entra') {
    try {
      const { msalInstance } = await import('../auth/authConfig');
      if (msalInstance) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          try {
            const tokenResponse = await msalInstance.acquireTokenSilent({
              scopes: ['api://ea494ba5-002a-4b79-8798-cee64fc10634/access_as_user'],
              account: accounts[0],
            });
            config.headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
          } catch (silentErr) {
            console.warn('Silent token acquisition failed, redirecting:', silentErr);
            msalInstance.acquireTokenRedirect({
              scopes: ['api://ea494ba5-002a-4b79-8798-cee64fc10634/access_as_user'],
            });
          }
        }
      }
    } catch (err) {
      console.error('Failed to acquire auth token:', err);
    }
  }
  return config;
});

// Inject selected database into every API request
api.interceptors.request.use(config => {
  const selectedDb = localStorage.getItem('pmd-selected-database');
  if (selectedDb) {
    config.headers['X-Database-Id'] = selectedDb;
  }
  return config;
});

export const adminUsersApi = {
  getAll: () => api.get('/api/auth/users')
};

export const authApi = {
  getMe: () => api.get('/api/auth/me')
};

// ===== Development APIs =====
export const devFilesApi = {
  // Task Log (SQL-backed via DevTrack)
  getTaskLogJson: () => api.get('/api/devtrack/tasks'),
  createTask: (task) => api.post('/api/devtrack/tasks', task),
  updateTask: (id, task) => api.put(`/api/devtrack/tasks/${id}`, task),
  deleteTask: (id) => api.delete(`/api/devtrack/tasks/${id}`),
  getTasksArchive: () => api.get('/api/devtrack/tasks/archive'),
  archiveCompletedTasks: () => api.post('/api/devtrack/tasks/archive'),
  // Dev Plan (SQL-backed via DevTrack)
  getDevPlan: () => api.get('/api/devtrack/plan'),
  updateDevPlanStep: (stepNumber, step) => api.put(`/api/devtrack/plan/step/${stepNumber}`, step),
  addDevPlanStep: (step) => api.post('/api/devtrack/plan', step),
  deleteDevPlanStep: (stepNumber) => api.delete(`/api/devtrack/plan/step/${stepNumber}`),
  moveDevPlanStep: (stepNumber, direction) => api.post(`/api/devtrack/plan/step/${stepNumber}/move`, { direction }),
  reassignDevPlanStep: (stepNumber, targetStep) => api.post(`/api/devtrack/plan/step/${stepNumber}/reassign`, { targetStep }),
  closeDevPlanStep: (stepNumber, summary, changes) => api.post(`/api/devtrack/plan/step/${stepNumber}/close`, { summary, changes }),
  reorderDevPlan: (items) => api.put('/api/devtrack/plan/reorder', items),
  // Rev Log (SQL-backed via DevTrack)
  getRevLog: () => api.get('/api/devtrack/revlog'),
  addRevision: (revision) => api.post('/api/devtrack/revlog', revision),
  updateRevision: (id, revision) => api.put(`/api/devtrack/revlog/${id}`, revision),
  deleteRevision: (id) => api.delete(`/api/devtrack/revlog/${id}`),
  // Schema & Dev Tools (still file-based)
  getSchema: () => api.get('/api/devfiles/schema'),
  getLiveSchema: () => api.get('/api/devfiles/schema/live'),
  getClaudeNotes: () => api.get('/api/devfiles/claude-notes'),
  updateClaudeNotes: (content) => api.put('/api/devfiles/claude-notes', { content }),
  // Module Configs (SQL-backed via AppCfg_DynamicModules)
  getModuleConfigs: () => api.get('/api/devfiles/module-configs'),
  getModuleConfig: (tableName) => api.get(`/api/devfiles/module-config/${tableName}`),
  saveModuleConfig: (tableName, data) => api.put(`/api/devfiles/module-config/${tableName}`, data),
  refreshModuleConfigSchema: (tableName) => api.post(`/api/devfiles/module-config/${tableName}/refresh-schema`),
  deleteModuleConfig: (tableName) => api.delete(`/api/devfiles/module-config/${tableName}`),
  // API status
  getTablesWithApi: () => api.get('/api/devfiles/tables-with-api'),
  // Table field names (lightweight — for FK target dropdowns)
  getTableFields: (tableName) => api.get(`/api/devfiles/table-fields/${tableName}`)
};

// ===== Dynamic API - Generic CRUD with auto FK resolution =====
export const dynamicApi = {
  getAll: (tableName, params = {}) => {
    const qs = new URLSearchParams();
    if (params.limit) qs.append('limit', params.limit);
    if (params.search) qs.append('search', params.search);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return api.get(`/api/dynamic/${tableName}${query}`);
  },
  getFiltered: (tableName, filterField, filterValue) =>
    api.get(`/api/dynamic/${tableName}?filterField=${filterField}&filterValue=${filterValue}`),
  getById: (tableName, id) => api.get(`/api/dynamic/${tableName}/${id}`),
  getLookups: (tableName, fieldName) => api.get(`/api/dynamic/${tableName}/lookups/${fieldName}`),
  create: (tableName, data) => api.post(`/api/dynamic/${tableName}`, data),
  update: (tableName, id, data) => api.put(`/api/dynamic/${tableName}/${id}`, data),
  delete: (tableName, id) => api.delete(`/api/dynamic/${tableName}/${id}`),
  deleteMultiple: (tableName, ids) =>
    Promise.all(ids.map(id => api.delete(`/api/dynamic/${tableName}/${id}`)))
};

// ===== KPI API - Dashboard aggregations =====
export const kpiApi = {
  getHome: () => api.get('/api/kpi/home'),
  getDocumentation: () => api.get('/api/kpi/documentation'),
  getNetworking: () => api.get('/api/kpi/networking'),
  getEquipment: () => api.get('/api/kpi/equipment')
};

// ===== App Configuration API =====
export const appCfgApi = {
  // Display Settings
  getDisplaySettings: () => api.get('/api/appcfg/display-settings'),
  getDisplaySetting: (key) => api.get(`/api/appcfg/display-settings/${key}`),
  updateDisplaySetting: (id, data) => api.put(`/api/appcfg/display-settings/${id}`, data),

  // Field Metadata
  getAllFieldMetadata: () => api.get('/api/appcfg/fieldmetadata'),
  getTableList: () => api.get('/api/appcfg/fieldmetadata/tables'),
  getFieldMetadata: (tableName) => api.get(`/api/appcfg/fieldmetadata/${tableName}`),
  updateFieldMetadata: (id, data) => api.put(`/api/appcfg/fieldmetadata/${id}`, data),
  updateFieldMetadataBatch: (fields) => api.put('/api/appcfg/fieldmetadata/batch', fields),

  // Sync
  syncFieldMetadata: () => api.post('/api/appcfg/sync'),

  // Database metadata
  getDatabaseViews: () => api.get('/api/appcfg/views'),
  getViewColumns: (viewName) => api.get(`/api/appcfg/views/${encodeURIComponent(viewName)}/columns`),

  // Lookup Categories
  getAllLookupCategories: () => api.get('/api/appcfg/lookup-categories'),
  getLookupCategory: (category) => api.get(`/api/appcfg/lookup-categories/${category}`),
  createLookupCategory: (data) => api.post('/api/appcfg/lookup-categories', data),
  updateLookupCategory: (category, data) => api.put(`/api/appcfg/lookup-categories/${category}`, data),
  deleteLookupCategory: (category) => api.delete(`/api/appcfg/lookup-categories/${category}`),

  // Lookup Values
  getAllLookups: () => api.get('/api/appcfg/lookups'),
  getLookupsByCategory: (category) => api.get(`/api/appcfg/lookups/${category}`),
  getLookupCategories: () => api.get('/api/appcfg/lookups/categories'),
  createLookup: (data) => api.post('/api/appcfg/lookups', data),
  updateLookup: (id, data) => api.put(`/api/appcfg/lookups/${id}`, data),
  deleteLookup: (id) => api.delete(`/api/appcfg/lookups/${id}`)
};

// ===== Database Sync API =====
export const syncApi = {
  start: (request) => api.post('/api/sync/start', request),
  getStatus: () => api.get('/api/sync/status'),
  getHistory: (project) => api.get(`/api/sync/history/${project}`),
  getAllHistory: () => api.get('/api/sync/history'),
  syncViews: () => api.post('/api/sync/sync-views')
};

// ===== Admin Management API (Step 37 - RBAC) =====
export const adminManagementApi = {
  // Summary
  getSummary: () => api.get('/api/admin/summary'),
  // Organizations
  getOrganizations: () => api.get('/api/admin/organizations'),
  getOrganization: (id) => api.get(`/api/admin/organizations/${id}`),
  createOrganization: (data) => api.post('/api/admin/organizations', data),
  updateOrganization: (id, data) => api.put(`/api/admin/organizations/${id}`, data),
  deleteOrganization: (id) => api.delete(`/api/admin/organizations/${id}`),
  // Databases
  getDatabases: () => api.get('/api/admin/databases'),
  getDatabase: (id) => api.get(`/api/admin/databases/${id}`),
  createDatabase: (data) => api.post('/api/admin/databases', data),
  updateDatabase: (id, data) => api.put(`/api/admin/databases/${id}`, data),
  deleteDatabase: (id) => api.delete(`/api/admin/databases/${id}`),
  // Permissions
  getPermissions: () => api.get('/api/admin/permissions'),
  getPermission: (id) => api.get(`/api/admin/permissions/${id}`),
  createPermission: (data) => api.post('/api/admin/permissions', data),
  updatePermission: (id, data) => api.put(`/api/admin/permissions/${id}`, data),
  deletePermission: (id) => api.delete(`/api/admin/permissions/${id}`),
  // Roles (DefRoles capability matrix)
  getDefRoles: () => api.get('/api/admin/roles'),
  getRoleMatrix: () => api.get('/api/admin/roles/matrix'),
  createDefRole: (data) => api.post('/api/admin/roles', data),
  updateDefRole: (id, data) => api.put(`/api/admin/roles/${id}`, data),
  updateRoleMatrix: (updates) => api.put('/api/admin/roles/matrix', updates),
  deleteDefRole: (id) => api.delete(`/api/admin/roles/${id}`),
  // User Roles
  getUserRoles: () => api.get('/api/admin/userroles'),
  getUserRolesForUser: (userId) => api.get(`/api/admin/userroles/user/${userId}`),
  createUserRole: (data) => api.post('/api/admin/userroles', data),
  updateUserRole: (id, data) => api.put(`/api/admin/userroles/${id}`, data),
  deleteUserRole: (id) => api.delete(`/api/admin/userroles/${id}`),
  // User Permissions (overrides)
  getUserPermissions: () => api.get('/api/admin/userpermissions'),
  getPermissionsForUserRole: (userRoleId) => api.get(`/api/admin/userpermissions/userrole/${userRoleId}`),
  createUserPermission: (data) => api.post('/api/admin/userpermissions', data),
  updateUserPermission: (id, data) => api.put(`/api/admin/userpermissions/${id}`, data),
  deleteUserPermission: (id) => api.delete(`/api/admin/userpermissions/${id}`),
  // Users (admin management view)
  getUsers: () => api.get('/api/admin/users'),
  getUser: (id) => api.get(`/api/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/api/admin/users/${id}`, data),
  copyUserRoles: (sourceId, targetId) => api.post(`/api/admin/users/${sourceId}/copy-to/${targetId}`),
  resetUserPermissions: (userId) => api.post(`/api/admin/users/${userId}/reset-permissions`),
  // Permission Resolution
  resolvePermissions: (userId, dbId) => api.get(`/api/admin/resolve/${userId}/${dbId}`),
  // Export
  exportUsers: () => api.get('/api/admin/export/users', { responseType: 'blob' }),
  exportRoleMatrix: () => api.get('/api/admin/export/role-matrix', { responseType: 'blob' }),
  // Import
  importUserRoles: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/admin/import/userroles', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Organization Images
  uploadOrgImage: (orgId, imageType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/admin/organizations/${orgId}/upload/${imageType}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteOrgImage: (orgId, imageType) => api.delete(`/api/admin/organizations/${orgId}/image/${imageType}`)
};

// ===== Database Selector API =====
export const databasesApi = {
  getAvailable: () => api.get('/api/databases/available')
};

// ===== Reporting API =====
export const reportingApi = {
  getConfig: (reportId) => api.get(`/api/report/${reportId}/config`),
  runReport: (reportId, params) => api.post(`/api/report/${reportId}/run`, params),
  getParameterOptions: (reportId, parameterField, filters) => {
    const params = {};
    if (filters) params.filters = JSON.stringify(filters);
    return api.get(`/api/report/${reportId}/parameter-options/${parameterField}`, { params });
  }
};

export default api;
